/**
 * Modelo de valores do formulário de pesquisa.
 *
 * O formulário trabalha com um objeto de campos SEMPRE presentes (strings,
 * arrays e booleanos com default) — bem mais simples de controlar do que o
 * `SearchFilters` cheio de opcionais. A conversão para `SearchFilters` (podando
 * vazios) acontece só no submit; a conversão inversa hidrata o formulário a
 * partir da URL (deep-link).
 */

import type { SearchFilters } from "@/types/api";

/** Item selecionado num combobox de busca (CNAE, natureza). */
export interface ComboboxItem {
  codigo: number;
  label: string;
}

export interface FilterFormValues {
  termo: string;
  /** Dígitos do campo CNPJ (0, 8 ou 14 quando válido). Chave única de busca. */
  cnpjInput: string;
  /**
   * Texto do campo de busca por sócio: nome exato (como na Receita) ou o CNPJ do
   * sócio pessoa jurídica. Modo alternativo — não vira `SearchFilters` (a busca
   * por sócio vive na rota `/socio`); quando preenchido, trava os demais campos.
   */
  socioInput: string;
  cnaeItems: ComboboxItem[];
  incluirCnaeSecundaria: boolean;
  naturezaItems: ComboboxItem[];
  situacao: string[];
  uf: string[];
  municipio: string[];
  bairro: string;
  cep: string;
  ddd: string;
  aberturaDe: string;
  aberturaAte: string;
  capitalDe: string;
  capitalAte: string;
  somenteMei: boolean;
  excluirMei: boolean;
  somenteMatriz: boolean;
  somenteFilial: boolean;
  comTelefone: boolean;
  somenteFixo: boolean;
  somenteCelular: boolean;
  comEmail: boolean;
  porte: string[];
}

/** Valores iniciais vazios do formulário. */
export function emptyFilterFormValues(): FilterFormValues {
  return {
    termo: "",
    cnpjInput: "",
    socioInput: "",
    cnaeItems: [],
    incluirCnaeSecundaria: false,
    naturezaItems: [],
    situacao: [],
    uf: [],
    municipio: [],
    bairro: "",
    cep: "",
    ddd: "",
    aberturaDe: "",
    aberturaAte: "",
    capitalDe: "",
    capitalAte: "",
    somenteMei: false,
    excluirMei: false,
    somenteMatriz: false,
    somenteFilial: false,
    comTelefone: false,
    somenteFixo: false,
    somenteCelular: false,
    comEmail: false,
    porte: [],
  };
}

function toIntArray(values: string[]): number[] {
  const out: number[] = [];
  for (const value of values) {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      out.push(parsed);
    }
  }
  return out;
}

/** Converte o texto do campo de capital (aceita vírgula) em número >= 0. */
function parseCapital(value: string): number | undefined {
  const normalized = value.trim().replace(/\./g, "").replace(",", ".");
  if (normalized === "") {
    return undefined;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

/** Converte os valores do formulário em `SearchFilters` (poda vazios). */
export function formValuesToFilters(values: FilterFormValues): SearchFilters {
  // Busca por CNPJ é chave única: quando preenchida, ignora todos os demais filtros.
  const cnpjDigits = values.cnpjInput;
  if (cnpjDigits.length === 14) {
    return { cnpj: cnpjDigits };
  }
  if (cnpjDigits.length === 8) {
    return { cnpjBasico: cnpjDigits };
  }
  if (cnpjDigits.length > 0) {
    // CNPJ incompleto — a pesquisa é bloqueada pela validação inline.
    return {};
  }

  const filters: SearchFilters = {};

  const termo = values.termo.trim();
  if (termo) {
    filters.termo = termo;
  }
  if (values.cnaeItems.length > 0) {
    filters.cnae = values.cnaeItems.map((item) => item.codigo);
    if (values.incluirCnaeSecundaria) {
      filters.incluirCnaeSecundaria = true;
    }
  }
  if (values.naturezaItems.length > 0) {
    filters.natureza = values.naturezaItems.map((item) => item.codigo);
  }
  const situacao = toIntArray(values.situacao);
  if (situacao.length > 0) {
    filters.situacao = situacao;
  }
  if (values.uf.length > 0) {
    filters.uf = values.uf;
  }
  const municipio = toIntArray(values.municipio);
  if (municipio.length > 0) {
    filters.municipio = municipio;
  }
  const bairro = values.bairro.trim();
  if (bairro) {
    filters.bairro = bairro;
  }
  const cep = values.cep.replace(/\D/g, "");
  if (cep) {
    filters.cep = cep;
  }
  const ddd = values.ddd.replace(/\D/g, "");
  if (ddd) {
    filters.ddd = ddd;
  }
  if (values.aberturaDe) {
    filters.aberturaDe = values.aberturaDe;
  }
  if (values.aberturaAte) {
    filters.aberturaAte = values.aberturaAte;
  }
  const capitalDe = parseCapital(values.capitalDe);
  if (capitalDe !== undefined) {
    filters.capitalDe = capitalDe;
  }
  const capitalAte = parseCapital(values.capitalAte);
  if (capitalAte !== undefined) {
    filters.capitalAte = capitalAte;
  }
  const porte = toIntArray(values.porte);
  if (porte.length > 0) {
    filters.porte = porte;
  }
  if (values.somenteMei) {
    filters.somenteMei = true;
  }
  if (values.excluirMei) {
    filters.excluirMei = true;
  }
  if (values.somenteMatriz) {
    filters.somenteMatriz = true;
  }
  if (values.somenteFilial) {
    filters.somenteFilial = true;
  }
  if (values.comTelefone) {
    filters.comTelefone = true;
  }
  if (values.somenteFixo) {
    filters.somenteFixo = true;
  }
  if (values.somenteCelular) {
    filters.somenteCelular = true;
  }
  if (values.comEmail) {
    filters.comEmail = true;
  }

  return filters;
}

/**
 * Hidrata os valores do formulário a partir de `SearchFilters`. Os itens de
 * CNAE/natureza (com rótulos) são fornecidos pelo chamador — os filtros só
 * guardam os códigos.
 */
export function filtersToFormValues(
  filters: SearchFilters,
  cnaeItems: ComboboxItem[],
  naturezaItems: ComboboxItem[],
): FilterFormValues {
  return {
    ...emptyFilterFormValues(),
    termo: filters.termo ?? "",
    cnpjInput: filters.cnpj ?? filters.cnpjBasico ?? "",
    cnaeItems,
    incluirCnaeSecundaria: filters.incluirCnaeSecundaria ?? false,
    naturezaItems,
    situacao: (filters.situacao ?? []).map(String),
    uf: filters.uf ?? [],
    municipio: (filters.municipio ?? []).map(String),
    bairro: filters.bairro ?? "",
    cep: filters.cep ?? "",
    ddd: filters.ddd ?? "",
    aberturaDe: filters.aberturaDe ?? "",
    aberturaAte: filters.aberturaAte ?? "",
    capitalDe: filters.capitalDe !== undefined ? String(filters.capitalDe) : "",
    capitalAte: filters.capitalAte !== undefined ? String(filters.capitalAte) : "",
    somenteMei: filters.somenteMei ?? false,
    excluirMei: filters.excluirMei ?? false,
    somenteMatriz: filters.somenteMatriz ?? false,
    somenteFilial: filters.somenteFilial ?? false,
    comTelefone: filters.comTelefone ?? false,
    somenteFixo: filters.somenteFixo ?? false,
    somenteCelular: filters.somenteCelular ?? false,
    comEmail: filters.comEmail ?? false,
    porte: (filters.porte ?? []).map(String),
  };
}
